import React, { useMemo, useState } from "react";
import { DonutChart } from "../../../../components/src/charts/DonutChart";
import {
    FundParseExposureChartsSummary,
    FundParseExposureDimensionKey,
    FundParseExposureSlice
} from "./fundParseTypes";
import {
    EXPOSURE_DIMENSION_OPTIONS,
    buildExposurePieColors,
    formatExposureAmount,
    formatExposureShare,
    getExposureChartSlices,
    getExposureDimensionFootnote,
    getExposureExcludedSummary
} from "./fundParseExposureChartUtils";

interface FundParseExposureChartsProps {
    summary: FundParseExposureChartsSummary | null;
}

function ExposureDoughnutChart({
    title,
    slices,
    currency
}: {
    title: string;
    slices: FundParseExposureSlice[];
    currency: string;
}): React.ReactElement {
    const totalChartValue = useMemo(() => slices.reduce((sum, slice) => sum + slice.chartValue, 0), [slices]);
    const chartSlices = useMemo(
        () =>
            slices.map((slice) => ({
                label: `${slice.label} (${formatExposureAmount(slice.netValue, currency)}, ${slice.rowCount} rows, ${formatExposureShare(slice.chartValue, totalChartValue)})`,
                value: slice.chartValue
            })),
        [currency, slices, totalChartValue]
    );

    if (!slices.length) {
        return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-2 text-sm font-medium text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500">No exposure data for this dimension.</p>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
            <DonutChart
                slices={chartSlices.map((slice, index) => ({
                    label: slices[index].label,
                    value: slice.value
                }))}
                colors={buildExposurePieColors(slices)}
                hole={0.45}
                showLegend={slices.length <= 6}
                labelMode="label+percent"
                ariaLabel={title}
                ariaDescription={chartSlices.map((slice) => slice.label).join("; ")}
                height={360}
            />
        </div>
    );
}

function ExcludedExposurePanel({
    currency,
    excluded
}: {
    currency: string;
    excluded: NonNullable<ReturnType<typeof getExposureExcludedSummary>>;
}): React.ReactElement {
    return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
            <div className="font-medium">{excluded.label}</div>
            <div className="mt-1 grid gap-1 sm:grid-cols-2">
                <div>
                    Net exposure: <strong>{formatExposureAmount(excluded.netValue, currency)}</strong>
                </div>
                <div>
                    Gross absolute exposure: <strong>{formatExposureAmount(excluded.grossAbsValue, currency)}</strong>
                </div>
                <div>
                    Rows: <strong>{excluded.rowCount}</strong>
                </div>
                <div>
                    {excluded.includesNegativeNet ? "Includes negative net market values." : "No negative net market values."}
                </div>
            </div>
            <p className="mt-2 text-xs text-amber-900">
                Derivatives and other excluded positions are not included in the doughnut slices above. Review the excluded-positions
                workbook when present.
            </p>
        </div>
    );
}

function ExposureChartCard({
    selectId,
    dimensionKey,
    onDimensionChange,
    summary
}: {
    selectId: string;
    dimensionKey: FundParseExposureDimensionKey;
    onDimensionChange: (value: FundParseExposureDimensionKey) => void;
    summary: FundParseExposureChartsSummary;
}): React.ReactElement {
    const dimension = summary.dimensions[dimensionKey];
    const slices = useMemo(() => getExposureChartSlices(dimensionKey, summary), [dimensionKey, summary]);
    const excluded = getExposureExcludedSummary(dimensionKey, summary);
    const footnote = getExposureDimensionFootnote(dimension);
    const selectedOption = EXPOSURE_DIMENSION_OPTIONS.find((option) => option.value === dimensionKey);

    return (
        <div className="min-w-0 space-y-3">
            <div>
                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={selectId}>
                    Chart dimension
                </label>
                <select
                    id={selectId}
                    className="form-input w-full"
                    value={dimensionKey}
                    onChange={(event) => onDimensionChange(event.target.value as FundParseExposureDimensionKey)}
                >
                    {EXPOSURE_DIMENSION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            <ExposureDoughnutChart
                title={selectedOption?.label || "Exposure"}
                slices={slices}
                currency={summary.reportingCurrency}
            />
            {footnote ? <p className="text-xs text-gray-600">{footnote}</p> : null}
            {dimensionKey === "bond_credit_quality_step" && dimension?.bondRowCount ? (
                <p className="text-xs text-gray-600">
                    Credit quality step reported on {dimension.bondRowsWithStep || 0} of {dimension.bondRowCount} bond rows.
                </p>
            ) : null}
            {excluded ? <ExcludedExposurePanel currency={summary.reportingCurrency} excluded={excluded} /> : null}
        </div>
    );
}

export function FundParseExposureCharts({ summary }: FundParseExposureChartsProps): React.ReactElement | null {
    const [leftDimension, setLeftDimension] = useState<FundParseExposureDimensionKey>("cic_bucket");
    const [rightDimension, setRightDimension] = useState<FundParseExposureDimensionKey>("issuer_country");

    if (!summary) {
        return null;
    }

    return (
        <div className="mt-8 rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-4">
                <h2 className="text-lg font-medium">Indirect exposure charts</h2>
                <p className="text-sm text-gray-600">
                    Consolidated TPT market values ({summary.measureColumn}) for valuation date {summary.valuationDate}.
                    {summary.reportingCurrency ? ` Amounts shown in ${summary.reportingCurrency}.` : null}
                    {summary.multiPortfolioCurrency
                        ? " Multiple portfolio currencies detected; amounts are not converted to a single client base currency."
                        : null}
                </p>
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
                <ExposureChartCard selectId="exposure-dimension-left" dimensionKey={leftDimension} onDimensionChange={setLeftDimension} summary={summary} />
                <ExposureChartCard selectId="exposure-dimension-right" dimensionKey={rightDimension} onDimensionChange={setRightDimension} summary={summary} />
            </div>

            <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600">
                Look-through total: {formatExposureAmount(summary.totals.lookthroughNetValue, summary.reportingCurrency)} · Excluded
                net: {formatExposureAmount(summary.totals.excludedNetValue, summary.reportingCurrency)} · Negative rows:{" "}
                {summary.totals.negativeRowCount}
            </div>
        </div>
    );
}
